import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ReloadOutlined } from '@ant-design/icons';
import { axiosInstance } from '../../config';
import { productEconomics } from '../Cost/costCalc';
import './WeeklySalesChart.css';

const DAY_MS = 24 * 60 * 60 * 1000;
const PERIOD_OPTIONS = [7, 15, 30];
const PERIOD_STORAGE_KEY = 'dashboardSalesPeriod';
const SALES_CACHE_KEY = 'dashboardWeeklySalesCacheV3';

const readPeriod = () => {
    try {
        const value = Number(localStorage.getItem(PERIOD_STORAGE_KEY));
        return PERIOD_OPTIONS.includes(value) ? value : 7;
    } catch {
        return 7;
    }
};

const persistPeriod = (value) => {
    try {
        localStorage.setItem(PERIOD_STORAGE_KEY, String(value));
    } catch {
        // Период останется выбранным до перезагрузки, если хранилище недоступно.
    }
};

const readAllSalesCache = () => {
    try {
        const cached = JSON.parse(localStorage.getItem(SALES_CACHE_KEY) || '{}');
        return cached && typeof cached === 'object' ? cached : {};
    } catch {
        return {};
    }
};

const readSalesCache = (period) => {
    const bucket = readAllSalesCache()[period] || {};
    return {
        orders: Array.isArray(bucket.orders) ? bucket.orders : [],
        updatedAt: bucket.updatedAt || null,
    };
};

const writeSalesCache = (period, orders, updatedAt) => {
    try {
        const next = {
            ...readAllSalesCache(),
            [period]: { orders, updatedAt },
        };
        localStorage.setItem(SALES_CACHE_KEY, JSON.stringify(next));
    } catch {
        // График продолжит работать, даже если локальное хранилище переполнено.
    }
};

const slimProducts = (products) =>
    (products || []).map((product) => ({
        offer_id: product.offer_id,
        price: product.price,
        quantity: product.quantity,
        color: product.color?._id ? { _id: product.color._id } : undefined,
    }));

const slimOrders = (orders) =>
    (orders || []).map((order) => ({
        in_process_at: order.in_process_at,
        status: order.status,
        Fin_price: order.Fin_price,
        products: slimProducts(order.products),
    }));

const toDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const parseOrderDate = (value) => {
    const datePart = String(value || '').split(/\s|\n/)[0];
    const [day, month, year] = datePart.split('.').map(Number);
    if (!day || !month || !year) return null;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const parseAmount = (value) => {
    const normalized = String(value ?? 0)
        .replace(/\s/g, '')
        .replace(',', '.');
    const amount = Number(normalized);
    return Number.isFinite(amount) ? amount : 0;
};

const formatMoney = (value) =>
    `${Math.round(Number(value || 0)).toLocaleString('ru-RU')} ₽`;

const formatPercent = (value) => `${Number(value).toFixed(1).replace('.', ',')}%`;

const periodTotalOf = (days) =>
    (days || []).reduce((sum, day) => sum + Number(day.total || 0), 0);

const MONTHLY_FORECAST_MULTIPLIER = {
    7: 4,
    15: 2,
    30: 1,
};

const formatAxisMoney = (value) => {
    const abs = Math.abs(value);
    if (abs >= 1000000) return `${(value / 1000000).toFixed(1).replace('.', ',')} млн`;
    if (abs >= 1000) return `${(value / 1000).toFixed(1).replace('.', ',')} тыс`;
    return String(Math.round(value));
};

const emptyDays = (period) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Array.from({ length: period }, (_, index) => {
        const date = new Date(today.getTime() - (period - 1 - index) * DAY_MS);
        return {
            key: toDateKey(date),
            date,
            total: 0,
            cancelledTotal: 0,
            orders: 0,
            cancelledOrders: 0,
            unmatched: 0,
        };
    });
};

const aggregateDays = (orders, valueOf, period) => {
    const result = emptyDays(period);
    const byDate = new Map(result.map((day) => [day.key, day]));

    orders.forEach((order) => {
        const key = parseOrderDate(order.in_process_at);
        const day = byDate.get(key);
        if (!day) return;

        const { total, unmatched } = valueOf(order);
        day.total += total;
        day.unmatched += unmatched;
        day.orders += 1;
        if (order.status === 'cancelled' || order.status === 'not_accepted') {
            day.cancelledTotal += total;
            day.cancelledOrders += 1;
        }
    });

    return result;
};

const WeeklyBarChart = ({
    title,
    subtitle,
    headingTotal,
    headingPercent,
    forecastTotal,
    days,
    period,
    onPeriodChange,
    selectedKey,
    onSelect,
    loading,
    error,
    onReload,
    reloadLabel,
    emptyText,
}) => {
    const selected = days.find((day) => day.key === selectedKey) || days[days.length - 1];
    const maxValue = Math.max(...days.map((day) => Math.abs(day.total)), 1);
    const todayKey = toDateKey(new Date());
    const dense = days.length > 7;
    const veryDense = days.length > 15;

    return (
        <section className="weekly-sales">
            <header className="weekly-sales__header">
                <div>
                    <h2>
                        {title}
                        <span
                            className={`weekly-sales__heading-total${
                                headingTotal < 0
                                    ? ' weekly-sales__heading-total--negative'
                                    : ''
                            }`}
                        >
                            {formatMoney(headingTotal)}
                            {headingPercent != null && (
                                <span
                                    className={`weekly-sales__heading-percent${
                                        headingPercent < 0
                                            ? ' weekly-sales__heading-percent--negative'
                                            : ''
                                    }`}
                                >
                                    {formatPercent(headingPercent)}
                                </span>
                            )}
                        </span>
                    </h2>
                    {forecastTotal != null && (
                        <div
                            className={`weekly-sales__forecast${
                                forecastTotal < 0 ? ' weekly-sales__forecast--negative' : ''
                            }`}
                        >
                            Прогноз за месяц
                            <strong>{formatMoney(forecastTotal)}</strong>
                        </div>
                    )}
                    <span>{subtitle}</span>
                </div>
                <div className="weekly-sales__period">
                    <div className="weekly-sales__period-options" role="group" aria-label="Период">
                        {PERIOD_OPTIONS.map((daysCount) => (
                            <button
                                key={daysCount}
                                type="button"
                                className={period === daysCount ? 'is-active' : ''}
                                onClick={() => onPeriodChange(daysCount)}
                            >
                                {daysCount}
                            </button>
                        ))}
                    </div>
                    <span>дней</span>
                    <button
                        type="button"
                        className="weekly-sales__reload"
                        onClick={onReload}
                        disabled={loading}
                        aria-label={reloadLabel}
                    >
                        <ReloadOutlined spin={loading} />
                    </button>
                </div>
            </header>

            {error && days.every((day) => day.orders === 0) && !loading ? (
                <div className="weekly-sales__error">{error}</div>
            ) : loading && days.every((day) => day.orders === 0) ? (
                <div className="weekly-sales__loading">{emptyText}</div>
            ) : (
                <>
                    {error && <div className="weekly-sales__stale-error">{error}</div>}
                    <div className="weekly-sales__tooltip">
                        <div>
                            <strong>
                                {selected.date.toLocaleDateString('ru-RU', {
                                    day: 'numeric',
                                    month: 'short',
                                })}
                            </strong>
                            {selected.key === todayKey && <span>Сегодня</span>}
                        </div>
                        <div
                            className={`weekly-sales__tooltip-total${
                                selected.total < 0
                                    ? ' weekly-sales__tooltip-total--negative'
                                    : ''
                            }`}
                        >
                            {formatMoney(selected.total)}
                        </div>
                        <div className="weekly-sales__tooltip-meta">
                            <span>{selected.orders} отправлений</span>
                            {selected.cancelledOrders > 0 && (
                                <span>
                                    Отменено: {selected.cancelledOrders} ·{' '}
                                    {formatMoney(selected.cancelledTotal)}
                                </span>
                            )}
                            {selected.unmatched > 0 && (
                                <span>Без себестоимости: {selected.unmatched}</span>
                            )}
                        </div>
                    </div>

                    <div className="weekly-sales__chart">
                        <div className="weekly-sales__grid" aria-hidden="true">
                            {[1, 0.66, 0.33, 0].map((ratio) => (
                                <div key={ratio}>
                                    <span>{formatAxisMoney(maxValue * ratio)}</span>
                                </div>
                            ))}
                        </div>
                        <div
                            className={`weekly-sales__bars${
                                dense ? ' weekly-sales__bars--dense' : ''
                            }${veryDense ? ' weekly-sales__bars--very-dense' : ''}`}
                            style={{ '--bar-count': days.length }}
                        >
                            {days.map((day, index) => {
                                const height = day.total
                                    ? Math.max(8, (Math.abs(day.total) / maxValue) * 100)
                                    : 3;
                                const isToday = day.key === todayKey;
                                const isSelected = day.key === selected.key;
                                const showLabel =
                                    !veryDense ||
                                    isToday ||
                                    isSelected ||
                                    index % 2 === 0;
                                return (
                                    <button
                                        key={day.key}
                                        type="button"
                                        className={`weekly-sales__bar-column${
                                            isToday ? ' weekly-sales__bar-column--today' : ''
                                        }${
                                            isSelected
                                                ? ' weekly-sales__bar-column--selected'
                                                : ''
                                        }`}
                                        onClick={() => onSelect(day.key)}
                                        aria-label={`${day.date.toLocaleDateString(
                                            'ru-RU'
                                        )}: ${formatMoney(day.total)}`}
                                    >
                                        <span className="weekly-sales__bar-track">
                                            <span
                                                className={`weekly-sales__bar-value${
                                                    day.total < 0
                                                        ? ' weekly-sales__bar-value--negative'
                                                        : ''
                                                }`}
                                                style={{ height: `${height}%` }}
                                            />
                                        </span>
                                        <span
                                            className={`weekly-sales__bar-label${
                                                showLabel
                                                    ? ''
                                                    : ' weekly-sales__bar-label--hidden'
                                            }`}
                                        >
                                            {day.date.getDate()}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
        </section>
    );
};

const WeeklySalesChart = () => {
    const initialPeriod = useMemo(readPeriod, []);
    const cachedSales = useMemo(() => readSalesCache(initialPeriod), [initialPeriod]);
    const [period, setPeriod] = useState(initialPeriod);
    const [orders, setOrders] = useState(cachedSales.orders);
    const [costItems, setCostItems] = useState([]);
    const [costCatalog, setCostCatalog] = useState([]);
    const [updatedAt, setUpdatedAt] = useState(cachedSales.updatedAt);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedKey, setSelectedKey] = useState(toDateKey(new Date()));
    const periodRef = useRef(period);
    periodRef.current = period;

    const loadCost = async () => {
        try {
            const [itemsResponse, catalogResponse] = await Promise.all([
                axiosInstance.get('/v1/cost/items'),
                axiosInstance.get('/v1/cost/catalog'),
            ]);
            setCostItems(itemsResponse.data || []);
            setCostCatalog(catalogResponse.data || []);
        } catch {
            setCostItems([]);
            setCostCatalog([]);
        }
    };

    const loadOrders = async (days) => {
        setLoading(true);
        setError('');
        try {
            const ordersResponse = await axiosInstance.get('/v1/orders/ozon/get_orders', {
                params: { hist: days, toExit: false, active: false },
            });
            if (days !== periodRef.current) return;
            const acceptedOrders = Array.isArray(ordersResponse.data?.orders)
                ? ordersResponse.data.orders
                : [];
            const cacheOrders = slimOrders(acceptedOrders);
            const acceptedAt = new Date().toISOString();
            setOrders(acceptedOrders);
            setUpdatedAt(acceptedAt);
            writeSalesCache(days, cacheOrders, acceptedAt);
        } catch (requestError) {
            if (days !== periodRef.current) return;
            setError(
                requestError.response?.data?.message ||
                    requestError.response?.data?.detail ||
                    'Не удалось загрузить продажи'
            );
        } finally {
            if (days === periodRef.current) setLoading(false);
        }
    };

    useEffect(() => {
        loadCost();
    }, []);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError('');
            try {
                const ordersResponse = await axiosInstance.get('/v1/orders/ozon/get_orders', {
                    params: { hist: period, toExit: false, active: false },
                });
                if (cancelled) return;
                const acceptedOrders = Array.isArray(ordersResponse.data?.orders)
                    ? ordersResponse.data.orders
                    : [];
                const cacheOrders = slimOrders(acceptedOrders);
                const acceptedAt = new Date().toISOString();
                setOrders(acceptedOrders);
                setUpdatedAt(acceptedAt);
                writeSalesCache(period, cacheOrders, acceptedAt);
            } catch (requestError) {
                if (cancelled) return;
                setError(
                    requestError.response?.data?.message ||
                        requestError.response?.data?.detail ||
                        'Не удалось загрузить продажи'
                );
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [period]);

    const handlePeriodChange = (days) => {
        if (days === period) return;
        persistPeriod(days);
        const cached = readSalesCache(days);
        if (cached.orders.length) {
            setOrders(cached.orders);
            setUpdatedAt(cached.updatedAt);
        } else {
            setOrders([]);
            setUpdatedAt(null);
        }
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const oldestKey = toDateKey(new Date(today.getTime() - (days - 1) * DAY_MS));
        setSelectedKey((prev) => (prev >= oldestKey ? prev : toDateKey(today)));
        setPeriod(days);
    };

    const updatedLabel = updatedAt
        ? ` · обновлено ${new Date(updatedAt).toLocaleTimeString('ru-RU', {
              hour: '2-digit',
              minute: '2-digit',
          })}`
        : '';

    const salesDays = useMemo(
        () =>
            aggregateDays(
                orders,
                (order) => ({
                    total: parseAmount(order.Fin_price),
                    unmatched: 0,
                }),
                period
            ),
        [orders, period]
    );

    const profitDays = useMemo(
        () =>
            aggregateDays(
                orders,
                (order) => {
                    let total = 0;
                    let unmatched = 0;
                    (order.products || []).forEach((product) => {
                        const econ = productEconomics(product, costItems, costCatalog);
                        if (!econ) {
                            unmatched += 1;
                            return;
                        }
                        total += econ.orderProfit;
                    });
                    return { total, unmatched };
                },
                period
            ),
        [orders, costItems, costCatalog, period]
    );

    const salesTotal = useMemo(() => periodTotalOf(salesDays), [salesDays]);
    const profitTotal = useMemo(() => periodTotalOf(profitDays), [profitDays]);
    const profitShare = salesTotal > 0 ? (profitTotal / salesTotal) * 100 : null;
    const profitForecast =
        profitTotal * (MONTHLY_FORECAST_MULTIPLIER[period] || 1);

    return (
        <>
            <WeeklyBarChart
                title="Продажи"
                subtitle={`Все отправления, включая отменённые${updatedLabel}`}
                headingTotal={salesTotal}
                days={salesDays}
                period={period}
                onPeriodChange={handlePeriodChange}
                selectedKey={selectedKey}
                onSelect={setSelectedKey}
                loading={loading}
                error={error}
                onReload={() => loadOrders(period)}
                reloadLabel="Обновить продажи"
                emptyText="Загружаем продажи…"
            />
            <WeeklyBarChart
                title="Чистейшая прибыль"
                subtitle={`По цене в заказе и правилам себестоимости${updatedLabel}`}
                headingTotal={profitTotal}
                headingPercent={profitShare}
                forecastTotal={profitForecast}
                days={profitDays}
                period={period}
                onPeriodChange={handlePeriodChange}
                selectedKey={selectedKey}
                onSelect={setSelectedKey}
                loading={loading}
                error={error}
                onReload={() => loadOrders(period)}
                reloadLabel="Обновить прибыль"
                emptyText="Загружаем прибыль…"
            />
        </>
    );
};

export default WeeklySalesChart;
